// frontend/src/components/AuditLogs.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Alert, Spinner, Badge, Form, Button, Row, Col } from 'react-bootstrap';
import api from '../api';

const ACTION_BADGE = {
  login_success: { bg: 'success', label: '✅ Connexion réussie' },
  login_failed:  { bg: 'danger',  label: '❌ Connexion échouée' },
  logout:        { bg: 'secondary', label: '🚪 Déconnexion' },
};

function AuditLogs() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [limit, setLimit]     = useState(50);
  const [filtreAction, setFiltreAction] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/auth/audit?limit=${limit}`);
      setLogs(response.data);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (val) => {
    if (!val) return '-';
    return new Date(val).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const parseDetails = (details) => {
    if (!details) return '-';
    try {
      const obj = typeof details === 'string' ? JSON.parse(details) : details;
      if (obj.reason) {
        const reasons = {
          wrong_password:   'Mauvais mot de passe',
          user_not_found:   'Utilisateur inconnu',
          account_disabled: 'Compte désactivé'
        };
        return reasons[obj.reason] || obj.reason;
      }
      return '-';
    } catch {
      return details;
    }
  };

  const logsFiltres = filtreAction
    ? logs.filter(l => l.action === filtreAction)
    : logs;

  // Stats rapides
  const nbSuccess  = logs.filter(l => l.action === 'login_success').length;
  const nbFailed   = logs.filter(l => l.action === 'login_failed').length;
  const nbLogout   = logs.filter(l => l.action === 'logout').length;

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des logs...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Row className="mb-4 mt-3">
        <Col md={3}>
          <div className="p-3 rounded text-center" style={{ background: '#d1e7dd' }}>
            <h5>✅ Connexions réussies</h5>
            <h3 className="text-success">{nbSuccess}</h3>
          </div>
        </Col>
        <Col md={3}>
          <div className="p-3 rounded text-center" style={{ background: '#f8d7da' }}>
            <h5>❌ Tentatives échouées</h5>
            <h3 className="text-danger">{nbFailed}</h3>
          </div>
        </Col>
        <Col md={3}>
          <div className="p-3 rounded text-center" style={{ background: '#e2e3e5' }}>
            <h5>🚪 Déconnexions</h5>
            <h3 className="text-secondary">{nbLogout}</h3>
          </div>
        </Col>
        <Col md={3}>
          <div className="p-3 rounded text-center" style={{ background: '#cfe2ff' }}>
            <h5>📋 Total événements</h5>
            <h3 className="text-primary">{logs.length}</h3>
          </div>
        </Col>
      </Row>

      {/* Barre d'outils */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Logs d'audit ({logsFiltres.length})</h4>
          <Form.Select size="sm" style={{ width: '200px' }}
            value={filtreAction} onChange={(e) => setFiltreAction(e.target.value)}>
            <option value="">Tous les événements</option>
            <option value="login_success">✅ Connexions réussies</option>
            <option value="login_failed">❌ Tentatives échouées</option>
            <option value="logout">🚪 Déconnexions</option>
          </Form.Select>
          <Form.Select size="sm" style={{ width: '130px' }}
            value={limit} onChange={(e) => setLimit(e.target.value)}>
            <option value="50">50 derniers</option>
            <option value="100">100 derniers</option>
            <option value="200">200 derniers</option>
          </Form.Select>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={fetchLogs}>
          🔄 Actualiser
        </Button>
      </div>

      {logsFiltres.length === 0 ? (
        <Alert variant="info">Aucun événement trouvé.</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Date / Heure</th>
              <th>Événement</th>
              <th>Utilisateur</th>
              <th>IP</th>
              <th>Détail</th>
            </tr>
          </thead>
          <tbody>
            {logsFiltres.map((log) => (
              <tr key={log.id}
                className={
                  log.action === 'login_failed'  ? 'table-danger'  :
                  log.action === 'login_success' ? 'table-success' : ''
                }>
                <td><small>{formatDate(log.created_at)}</small></td>
                <td>
                  <Badge bg={ACTION_BADGE[log.action]?.bg || 'secondary'}>
                    {ACTION_BADGE[log.action]?.label || log.action}
                  </Badge>
                </td>
                <td>{log.username || <span className="text-muted">inconnu</span>}</td>
                <td><code>{log.ip || '-'}</code></td>
                <td><small className="text-muted">{parseDetails(log.details)}</small></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

export default AuditLogs;